import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Box,
  Typography,
  Button,
  Avatar,
  TextField,
  Switch,
  IconButton,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import star from "../../assets/points.svg";
import edit from "../../assets/pen.png";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import insta from "../../assets/instaf.png";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import fb from "../../assets/f.png";
import twit from "../../assets/twitf.png";
import linkedin from "../../assets/linkedin.png";
import noImage from "../../assets/noimage.jpg";
import whatsapp from "../../assets/whatsapp.svg";
import youtube from "../../assets/socialmedia/youtube.svg";
import CleanCaptionInput from "./CaptionInput";
import HashtagInput from "./HashTagInput";
import EditorBox from "../Layout/Postcreation/Editor";
import {
  createXExternalPost,
  createFacebookExternalPost,
  createLinkedinExternalPost,
  createInstagramExternalPost,
  createYoutubeExternalPost,
  scheduleExternalPost,
  postSummary,
} from "../../services/postService";
import { toast } from "react-toastify";
import CategoryListing from "./categoriesList";
import { Auth } from "../../contexts/AuthContext";
import {
  clampPlatformContent,
  composePlatformContent,
} from "../../shared/businessPageComposer";
import {
  BLACK_THUMBNAIL,
  getBunnyEmbedUrl,
  getResolvedImageUrl,
  getResolvedPosterUrl,
  getResolvedVideoUrl,
  isBunnyStreamMedia,
} from "../../shared/mediaResolver";
import { useNavigate } from "react-router-dom";
import { buildPublicShareUrl } from "../../services/runtimeConfig";
import TeaLoadingModal from "../teaLoadingModal/TeaLoadingModal";

const getSystemHashtags = (post = {}) => {
  const candidates = [post?.hashtags, post?.hashTags, post?.postHashtags];
  const source = candidates.find((value) => Array.isArray(value)) || [];
  return source
    .map((tag) => {
      if (typeof tag === "string") {
        return tag.trim();
      }
      return (
        tag?.tag ||
        tag?.name ||
        tag?.value ||
        tag?.hashtag ||
        ""
      ).trim();
    })
    .filter(Boolean);
};

const appendMediaToWhatsAppText = (text, post) => {
  // 1. Clean the caption: replace/remove broken lnkd.in links
  let cleanText = text || "";
  if (post?.featuredUrl && !post.featuredUrl.startsWith("urn:")) {
    cleanText = cleanText.replace(/https?:\/\/lnkd\.in\/[a-zA-Z0-9]+/g, post.featuredUrl);
  } else {
    cleanText = cleanText.replace(/https?:\/\/lnkd\.in\/[a-zA-Z0-9]+/g, "");
  }

  let finalMsg = cleanText.trim();

  // 2. Extract direct media link
  const imageMedia = post?.media?.find((m) => m?.mediaType?.toUpperCase() === "IMAGE");
  const videoMedia = post?.media?.find((m) => m?.mediaType?.toUpperCase() === "VIDEO");
  const mediaUrl =
    (post?.featuredUrl && !post.featuredUrl.startsWith("urn:") ? post.featuredUrl : null) ||
    (imageMedia ? getResolvedImageUrl(imageMedia) : null) ||
    (videoMedia ? getResolvedVideoUrl(videoMedia) : null);

  // 3. Append the public OG share-page URL so WhatsApp shows a rich image preview card.
  const shareUrl = buildPublicShareUrl(post?.id);
  if (shareUrl) {
    finalMsg += `\n\n${shareUrl}`;
    if (mediaUrl && mediaUrl !== post?.featuredUrl) {
      finalMsg += `\n\nMedia Link: ${mediaUrl}`;
    }
  } else if (mediaUrl) {
    finalMsg += `\n\n${mediaUrl}`;
  }

  return finalMsg.trim();
};

const getParsedVariations = (content) => {
  if (!content) return null;
  if (Array.isArray(content)) return content;
  try {
    const str = String(content).trim();
    if (!str.startsWith("[")) return null;
    const escaped = str.replace(/\n/g, "\\n").replace(/\r/g, "\\r").replace(/\t/g, "\\t");
    const parsed = JSON.parse(escaped);
    if (Array.isArray(parsed) && parsed.length > 0 && (parsed[0].content !== undefined || (parsed[0].id && String(parsed[0].id).startsWith("var_")))) {
      return parsed;
    }
  } catch (e) {
    // Not valid JSON
  }
  return null;
};

const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentTimeString = () => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const SharePostPopup = ({
  open,
  onClose,
  type,
  title,
  postReviewData,
  clear,
  selected,
  summary,
  personalConnections = {},
  initialVariationIndex = 0,
}) => {
  const parsedVariations = getParsedVariations(postReviewData?.preparedPost?.content);
  const variations = (Array.isArray(postReviewData?.preparedPost?.variations) && postReviewData.preparedPost.variations.length > 0)
    ? postReviewData.preparedPost.variations
    : (parsedVariations || []);
  const hasVariations = variations.length > 0;
  const [selectedVariationIndex, setSelectedVariationIndex] = useState(initialVariationIndex);
  const [connectDialogPlatform, setConnectDialogPlatform] = useState(null);
  const navigate = useNavigate();

  const platFormNewData = [
    "Linkedin",
    "Twitter/X",
    // "Instagram",
    // "Youtube",
    // "Facebook",
    // "WhatsApp"
  ];
  const isAllSelected = selected?.includes("All");
  const [platformData, setPlatformData] = useState(() => {
    const initialData = {};
    platFormNewData.forEach((platform) => {
      const keyMap = {
        Linkedin: "LINKEDIN",
        "Twitter/X": "X",
        Instagram: "INSTAGRAM",
        Youtube: "YOUTUBE",
        Facebook: "FACEBOOK",
      };

      const defaultContent = hasVariations
        ? (variations[selectedVariationIndex]?.content || "")
        : (postReviewData?.platformContentMap?.[keyMap[platform]] || postReviewData?.preparedPost?.content || "");

      const pointsKey = platform === "Twitter/X" ? "xpoints" : platform.toLowerCase() + "Points";
      const platformPoints =
        postReviewData?.estimatedLoyaltyPoints?.[pointsKey] ||
        postReviewData?.estimatedLoyaltyPoints?.[platform.toLowerCase() + "Points"] ||
        postReviewData?.estimatedLoyaltyPoints?.xPoints ||
        "10";

      initialData[platform] = {
        enabled: isAllSelected || selected?.includes(platform),
        thoughts: defaultContent,
        hashtags: [],
        showEditor: false,
        aiResult: "",
        caption: type === "post" ? defaultContent : summary,
        des: defaultContent,
        points: platformPoints,
      };
    });
    return initialData;
  });

  const totalCoins = useMemo(() => {
    if (!platformData) return postReviewData?.estimatedLoyaltyPoints?.total || 0;

    const enabledPlatforms = Object.values(platformData).filter((p) => p?.enabled);
    if (enabledPlatforms.length > 0) {
      return enabledPlatforms.reduce((acc, curr) => acc + (parseInt(curr?.points, 10) || 0), 0);
    }

    return Object.values(platformData).reduce((acc, curr) => acc + (parseInt(curr?.points, 10) || 0), 0);
  }, [platformData, postReviewData]);

  useEffect(() => {
    if (open) {
      setSelectedVariationIndex(initialVariationIndex);
    }
  }, [open, initialVariationIndex, postReviewData]);

  useEffect(() => {
    if (open && postReviewData?.preparedPost) {
      const selectedVar = hasVariations ? variations[selectedVariationIndex] : null;
      setPlatformData((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((platform) => {
          const keyMap = {
            Linkedin: "LINKEDIN",
            "Twitter/X": "X",
            Instagram: "INSTAGRAM",
            Youtube: "YOUTUBE",
            Facebook: "FACEBOOK",
          };
          const contentToUse = selectedVar
            ? (selectedVar.content || "")
            : (postReviewData?.platformContentMap?.[keyMap[platform]] || postReviewData?.preparedPost?.content || "");

          const pointsKey = platform === "Twitter/X" ? "xpoints" : platform.toLowerCase() + "Points";
          const platformPoints =
            postReviewData?.estimatedLoyaltyPoints?.[pointsKey] ||
            postReviewData?.estimatedLoyaltyPoints?.[platform.toLowerCase() + "Points"] ||
            postReviewData?.estimatedLoyaltyPoints?.xPoints ||
            "10";

          next[platform] = {
            ...next[platform],
            thoughts: contentToUse,
            caption: type === "post" ? contentToUse : summary,
            des: contentToUse,
            points: platformPoints,
          };
        });
        return next;
      });
    }
  }, [selectedVariationIndex, hasVariations, open, postReviewData]);

  const [thoughts, setThoughts] = useState("");

  const [showEditor, setShowEditor] = useState(false);
  const { businessPages, refreshLoyaltyPoints } = Auth();

  // Ensure unconnected platforms are never selected by default
  useEffect(() => {
    if (open) {
      setPlatformData((prev) => {
        let updated = false;
        const next = { ...prev };
        Object.keys(next).forEach((platformName) => {
          if (next[platformName].enabled) {
            const keyMap = {
              Linkedin: "LINKEDIN",
              "Twitter/X": "X",
              Instagram: "INSTAGRAM",
              Youtube: "YOUTUBE",
              Facebook: "FACEBOOK",
            };
            const mappedKey = keyMap[platformName];
            if (mappedKey) {
              const isConnected = (businessPages && Object.keys(businessPages).some(
                k => k.toUpperCase() === mappedKey
              )) || personalConnections[mappedKey];
              if (!isConnected) {
                next[platformName] = { ...next[platformName], enabled: false };
                updated = true;
              }
            }
          }
        });
        return updated ? next : prev;
      });
    }
  }, [open, businessPages]);

  const [loader, setLoader] = useState(false);
  const systemHashtags = getSystemHashtags(postReviewData?.preparedPost);

  const handleThoughtChange = (platformName, value) => {
    const nextValue = platformName === "WhatsApp"
      ? value.slice(0, 4096)
      : clampPlatformContent(value, platformName, businessPages, systemHashtags);
    setPlatformData((prev) => ({
      ...prev,
      [platformName]: {
        ...prev[platformName],
        thoughts: nextValue,
      },
    }));
    setThoughts(nextValue);
  };
  const handleCaptionChange = (platformName, value) => {
    const nextValue = platformName === "WhatsApp"
      ? value.slice(0, 4096)
      : clampPlatformContent(value, platformName, businessPages, systemHashtags);
    setPlatformData((prev) => ({
      ...prev,
      [platformName]: {
        ...prev[platformName],
        caption: nextValue,
      },
    }));
  };

  const handleToggleEditor = (platformName) => {
    setPlatformData((prev) => ({
      ...prev,
      [platformName]: {
        ...prev[platformName],
        showEditor: !prev[platformName].showEditor,
      },
    }));
    aiCaptionApiCall(platformName);
  };
  const [aiLoader, setAiLoader] = useState({});
  const [showGeneratingPopup, setShowGeneratingPopup] = useState(false);
  const [dots, setDots] = useState(".");
  useEffect(() => {
    let interval;
    if (aiLoader) {
      interval = setInterval(() => {
        setDots((prev) => (prev.length >= 3 ? "." : prev + "."));
      }, 1000);
    } else {
      setDots(".");
    }
    return () => clearInterval(interval);
  }, [aiLoader]);
  const aiCaptionApiCall = async (platformName) => {
    try {
      const xComposition = composePlatformContent("", "Twitter/X", businessPages, systemHashtags);
      const payload = {
        postId: postReviewData?.preparedPost?.id,
        userText: thoughts,
        xMaxCharacters: xComposition.maxBaseCharacters || 200,
      };
      setAiLoader((prev) => ({ ...prev, [platformName]: true }));
      setShowGeneratingPopup(true);
      const response = await postSummary(payload);
      if (response?.status === true) {
        const summarizedContent =
          response?.generateContentResponseDTO?.summarizedContent || "";
        const xSummarizedContent =
          response?.generateContentResponseDTO?.xSummarizedContent || "";

        // 👇 Update all platform captions
        setPlatformData((prevData) => {
          const updatedData = { ...prevData };
          Object.keys(updatedData).forEach((platform) => {
            // Use xsummarizedContent for Twitter/X, summarizedContent for others
            const contentToUse = platform === "Twitter/X" ? xSummarizedContent : summarizedContent;
            updatedData[platform] = {
              ...updatedData[platform],
              thoughts: contentToUse,
            };
          });
          return updatedData;
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setAiLoader((prev) => ({ ...prev, [platformName]: false }));
      setShowGeneratingPopup(false);
    }
  };

  const handleHashtagsChange = (platformName, tags) => {
    setPlatformData((prev) => ({
      ...prev,
      [platformName]: {
        ...prev[platformName],
        hashtags: tags,
      },
    }));
  };

  const getPlatformComposition = (platformName, content) => {
    if (platformName === "WhatsApp") {
      const text = content || "";
      const baseLength = Array.from(text).length;
      return {
        platformKey: "whatsapp",
        limit: 4096,
        suffixMode: "none",
        suffixText: "",
        finalText: text,
        baseLength,
        reservedLength: 0,
        effectiveLength: baseLength,
        maxBaseCharacters: 4096,
        remainingCharacters: Math.max(4096 - baseLength, 0),
        isOverLimit: baseLength > 4096,
        isSuffixTooLong: false,
      };
    }
    return composePlatformContent(content || "", platformName, businessPages, systemHashtags);
  };

  const getPlatformDisplayLimit = (platformName) => {
    const composition = getPlatformComposition(platformName, "");
    return composition.platformKey === "x"
      ? composition.maxBaseCharacters
      : composition.limit;
  };

  const getPlatformLimitNote = (platformName) => {
    const composition = getPlatformComposition(platformName, "");
    if (composition.platformKey !== "x" || !composition.reservedLength) {
      return "";
    }
    return `${composition.reservedLength} characters reserved for your business handle/link`;
  };

  const getVariationMedia = () => {
    const allPostMedia = postReviewData?.preparedPost?.media || [];
    if (!variations || variations.length === 0) return allPostMedia;
    const selectedVar = variations[selectedVariationIndex];
    if (!selectedVar) return allPostMedia;

    const hasVariationId = allPostMedia.some((m) => m?.variationId);
    if (hasVariationId) {
      return allPostMedia.filter((m) => m?.variationId === selectedVar?.id);
    } else {
      let offset = 0;
      for (let i = 0; i < variations.length; i++) {
        const count =
          (variations[i].libraryMediaIds?.length || 0) +
          (variations[i].mediaIds?.length || 0);
        if (i === selectedVariationIndex) {
          return count > 0 ? allPostMedia.slice(offset, offset + count) : [];
        }
        offset += count;
      }
      return [];
    }
  };

  const currentMediaItems = getVariationMedia();

  const platforms = [
    {
      name: "Linkedin",
      icon: linkedin,
      maxChar: "3,000",
      sampleImages: currentMediaItems,
      points: postReviewData?.estimatedLoyaltyPoints?.linkedinPoints || "",
    },
    {
      name: "Twitter/X",
      icon: twit,
      maxChar: String(getPlatformDisplayLimit("Twitter/X")),
      sampleImages: currentMediaItems,
      points: postReviewData?.estimatedLoyaltyPoints?.xpoints || "",
      note: getPlatformLimitNote("Twitter/X"),
    },
  ];

  const [currentIndexes, setCurrentIndexes] = useState(platforms.map(() => 0));
  const platformCount = platforms.length;

  const handleImageChange = (platformIndex, direction) => {
    setCurrentIndexes((prevIndexes) => {
      const updatedIndexes = [...prevIndexes];
      const totalImages = platforms[platformIndex].sampleImages.length;
      updatedIndexes[platformIndex] =
        (updatedIndexes[platformIndex] + direction + totalImages) % totalImages;
      return updatedIndexes;
    });
    setActiveBunnyPlayers((prev) => {
      const next = { ...prev };
      delete next[platformIndex];
      return next;
    });
  };

  const platformKeyMap = {
    "Twitter/X": "x",
    Facebook: "facebook",
    Instagram: "instagram",
    Linkedin: "linkedin",
  };

  const isBusinessPageConnection = (platform) => {
    const key = platformKeyMap[platform];
    return key && businessPages?.[key]?.oauthSourcePage === "BUSINESS";
  };

  const handlePost = async () => {
    try {
      setLoader(true);
      const apiMap = {
        "Twitter/X": createXExternalPost,
        Facebook: createFacebookExternalPost,
        Instagram: createInstagramExternalPost,
        Linkedin: createLinkedinExternalPost,
        Youtube: createYoutubeExternalPost,
      };

      // Check if any platform is enabled
      const enabledPlatforms = Object.entries(platformData).filter(
        ([_, data]) => data.enabled
      );

      if (enabledPlatforms.length === 0) {
        toast.warning("Please select at least one platform to post.");
        setLoader(false);
        return;
      }

      // Track success/failure
      let successCount = 0;
      let failedPlatforms = [];

      for (const [platform, data] of Object.entries(platformData)) {
        if (data.enabled) {
          if (isBusinessPageConnection(platform)) {
            toast.error(`${platform} is connected as a business page. Please connect your personal account to post.`);
            failedPlatforms.push(platform);
            continue;
          }
          try {
            const composition = getPlatformComposition(platform, data.caption || "");
            if (composition.isSuffixTooLong || composition.isOverLimit) {
              toast.error(
                composition.platformKey === "x"
                  ? `${platform} content exceeds the allowed character limit after adding your business handle/link.`
                  : `${platform} content exceeds the allowed character limit.`
              );
              failedPlatforms.push(platform);
              continue;
            }
            const payload = {
              postId: postReviewData?.preparedPost?.id,
              type: "POST",
              content: composition.finalText,
              selectedVariationId: hasVariations ? variations[selectedVariationIndex]?.id : null,
            };
            const response = await apiMap[platform](payload);
            if (response?.status === true) {
              toast.success(`${platform} post created successfully`);
              successCount++;
            } else {
              toast.error(response?.message || `${platform} post failed. Please check if the platform is linked.`);
              failedPlatforms.push(platform);
            }
          } catch (platformError) {
            toast.error(platformError.message || `${platform} post failed. Please try again.`);
            failedPlatforms.push(platform);
          }
        }
      }

      // Only clear and close on success
      if (successCount > 0) {
        if (refreshLoyaltyPoints) refreshLoyaltyPoints();
        clear();
        onClose();
      }

      // Summary message if partial failure
      if (failedPlatforms.length > 0 && successCount > 0) {
        toast.warning(`Some platforms failed: ${failedPlatforms.join(", ")}`);
      }

    } catch (error) {
      console.log(error);
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoader(false);
    }
  };
  const handleShare = async () => {
    try {
      setLoader(true);
      const apiMap = {
        "Twitter/X": createXExternalPost,
        Facebook: createFacebookExternalPost,
        Instagram: createInstagramExternalPost,
        Linkedin: createLinkedinExternalPost,
        Youtube: createYoutubeExternalPost,
      };

      // Check if any platform is enabled
      const enabledPlatforms = Object.entries(platformData).filter(
        ([_, data]) => data.enabled
      );

      if (enabledPlatforms.length === 0) {
        toast.warning("Please select at least one platform to share.");
        setLoader(false);
        return;
      }

      // Track success/failure
      let successCount = 0;
      let failedPlatforms = [];

      for (const [platform, data] of Object.entries(platformData)) {
        if (data.enabled) {
          if (isBusinessPageConnection(platform)) {
            toast.error(`${platform} is connected as a business page. Please connect your personal account to share.`);
            failedPlatforms.push(platform);
            continue;
          }
          try {
            const composition = getPlatformComposition(platform, data.thoughts || "");
            if (composition.isSuffixTooLong || composition.isOverLimit) {
              toast.error(
                composition.platformKey === "x"
                  ? `${platform} content exceeds the allowed character limit after adding your business handle/link.`
                  : `${platform} content exceeds the allowed character limit.`
              );
              failedPlatforms.push(platform);
              continue;
            }
            const payload = {
              postId: postReviewData?.preparedPost?.id,
              type: "SHARE",
              content: composition.finalText,
              selectedVariationId: hasVariations ? variations[selectedVariationIndex]?.id : null,
            };
            const response = await apiMap[platform](payload);
            if (response?.status === true) {
              toast.success(`${platform} shared successfully`);
              successCount++;
            } else {
              toast.error(response?.message || `${platform} share failed. Please check if the platform is linked.`);
              failedPlatforms.push(platform);
            }
          } catch (platformError) {
            toast.error(platformError.message || `${platform} share failed. Please try again.`);
            failedPlatforms.push(platform);
          }
        }
      }

      // Only clear and close on success
      if (successCount > 0) {
        if (refreshLoyaltyPoints) refreshLoyaltyPoints();
        clear();
        onClose();
      }

      // Summary message if partial failure
      if (failedPlatforms.length > 0 && successCount > 0) {
        toast.warning(`Some platforms failed: ${failedPlatforms.join(", ")}`);
      }

    } catch (error) {
      console.log(error);
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoader(false);
    }
  };
  const handleGenerateClick = () => {
    setShowEditor(!showEditor);
  };

  const handleTogglePlatform = (platformName) => {
    // Check if turning ON
    if (!platformData[platformName].enabled) {
      const keyMap = {
        Linkedin: "LINKEDIN",
        "Twitter/X": "X",
        Instagram: "INSTAGRAM",
        Youtube: "YOUTUBE",
        Facebook: "FACEBOOK",
      };

      const mappedKey = keyMap[platformName];
      if (mappedKey) {
        // Check if the platform exists in businessPages
        const isConnected = (businessPages && Object.keys(businessPages).some(
          k => k.toUpperCase() === mappedKey
        )) || personalConnections[mappedKey];

        if (!isConnected) {
          setConnectDialogPlatform(platformName);
          return;
        }
      }
    }

    setPlatformData((prev) => ({
      ...prev,
      [platformName]: {
        ...prev[platformName],
        enabled: !prev[platformName].enabled,
      },
    }));
  };
  const [schedule, setSchedule] = useState(false);
  const [startDate, setStartDate] = useState(getTodayDateString);
  const [startTime, setStartTime] = useState(getCurrentTimeString);
  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);

  useEffect(() => {
    if (schedule) {
      setStartDate(getTodayDateString());
      setStartTime(getCurrentTimeString());
    }
  }, [schedule]);

  // Format date for display (e.g., "Jan 8, 2025")
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const schedulePost = async () => {
    try {
      setLoader(true);
      const enabledPlatforms = [];
      const platformContents = {};

      for (const [platform, data] of Object.entries(platformData)) {
        if (data.enabled) {
          enabledPlatforms.push(platform.toUpperCase());
          platformContents[platform] = data.caption;
        }
      }

      // Combine date + time (Without 'Z', the browser treats it as the user's local time)
      const combinedDateTime = new Date(`${startDate}T${startTime}:00`);

      const payload = {
        postId: postReviewData?.preparedPost?.id,
        type: "POST",
        content: Object.values(platformContents)[0] || postReviewData?.preparedPost?.content || "",
        platforms: enabledPlatforms,
        scheduledTimeUtc: combinedDateTime.toISOString().split(".")[0] + "Z",
      };

      const response = await scheduleExternalPost(payload);
      if (response?.status === true) {
        toast.success("Post scheduled successfully");
        onClose();
        clear();
      } else {
        toast.error("Failed to schedule post");
      }
    } catch (error) {
      console.error("❌ Error scheduling post:", error);
      toast.error("Something went wrong while scheduling");
    } finally {
      setLoader(false);
    }
  };

  // const [schedule, setSchedule] = useState(false);
  // const [startDate, setStartDate] = useState("2025-05-12");
  // const dateInputRef = useRef(null);
  // const schedulePost = async () => {
  //   try {
  //     setLoader(true);
  //     const enabledPlatforms = [];
  //     const platformContents = {};
  //     for (const [platform, data] of Object.entries(platformData)) {
  //       if (data.enabled) {
  //         enabledPlatforms.push(platform.toUpperCase());
  //         platformContents[platform] = data.caption;
  //       }
  //     }
  //     const payload = {
  //       postId: postReviewData?.preparedPost?.id,
  //       type: "POST",
  //       content: "",
  //       platforms: enabledPlatforms,
  //       scheduledTimeUtc: startDate
  //         ? new Date(startDate).toISOString().split(".")[0] + "Z"
  //         : new Date().toISOString().split(".")[0] + "Z",
  //     };
  //     const response = await scheduleExternalPost(payload);
  //     if (response?.status === true) {
  //       toast.success("Post scheduled successfully");
  //       onClose();
  //       clear();
  //     } else {
  //       toast.error("Failed to schedule post");
  //     }
  //   } catch (error) {
  //     console.error("❌ Error scheduling post:", error);
  //     toast.error("Something went wrong while scheduling");
  //   } finally {
  //     setLoader(false);
  //   }
  // };

  const handleCloseSchedule = () => {
    setSchedule(false);
  };

  const handleCloseMainPopup = () => {
    clear();
    onClose();
  };

  const [playingIndex, setPlayingIndex] = useState(null);
  const [activeBunnyPlayers, setActiveBunnyPlayers] = useState({});
  const videoRefs = useRef({});

  useEffect(() => {
    setPlayingIndex(null);
    setActiveBunnyPlayers({});
    setCurrentIndexes(Array.from({ length: platformCount }, () => 0));
  }, [open, platformCount, postReviewData?.preparedPost?.id]);

  const handlePlayToggle = (index) => {
    const video = videoRefs.current[index];
    if (!video) return;

    if (playingIndex === index) {
      video.pause();
      setPlayingIndex(null);
    } else {
      // Pause other videos
      Object.values(videoRefs.current).forEach((v) => v && v.pause());
      video.play();
      setPlayingIndex(index);
    }
  };

  const handleActivateBunnyPlayer = (platformIndex) => {
    setActiveBunnyPlayers((prev) => ({
      ...prev,
      [platformIndex]: true,
    }));
  };
  return (
    <>
      <Dialog
        elevation={0}
        open={schedule}
        maxWidth="sm"
        PaperProps={{
          sx: {
            width: { xs: "90vw", sm: "500px", md: "600px" },
            p: 0,
            m: 0,
            borderRadius: 2,
          },
        }}
        onClose={handleCloseSchedule}
      >
        <Box sx={{ backgroundColor: "#f6f3ff", height: "auto", pb: 2 }}>
          {/* Header */}
          <Box
            bgcolor={"#ffffff"}
            boxShadow={"0px 4px 20px rgba(236, 236, 236, 0.25)"}
            display="flex"
            padding={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" flexDirection="column">
              <Typography
                variant="h6"
                color="rgba(59, 59, 59, 1)"
                fontWeight={500}
                fontSize="20px"
              >
                Schedule this Post
              </Typography>
              <Typography variant="body2" color="#95919D" fontSize="13px" mt={0.5}>
                Select the date and time when you want this post to be visible on the timeline.
              </Typography>
            </Box>
            <IconButton
              sx={{ width: "24px", height: "24px" }}
              onClick={handleCloseSchedule}
            >
              <CloseIcon sx={{ color: "rgba(138, 138, 138, 1)" }} />
            </IconButton>
          </Box>

          {/* Date & Time Fields */}
          <Box display={"flex"} flexWrap={"wrap"}>
            {/* Date */}
            <Grid item xs={6} sx={{ padding: "30px", position: "relative" }}>
              <Typography variant="body2" color="#95919D" mb={1}>
                Select Date
              </Typography>
              <TextField
                fullWidth
                value={formatDateForDisplay(startDate)}
                variant="standard"
                onClick={() => dateInputRef.current?.showPicker()}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <label htmlFor="start-date-picker">
                        <CalendarMonthOutlinedIcon
                          sx={{ color: "#1976d2", cursor: "pointer" }}
                        />
                      </label>
                    </InputAdornment>
                  ),
                }}
              />
              <input
                type="date"
                id="start-date-picker"
                ref={dateInputRef}
                value={startDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  position: "absolute",
                  top: 50,
                  left: 0,
                  opacity: 0,
                  width: 0,
                  height: 0,
                  pointerEvents: "auto",
                }}
              />
            </Grid>

            {/* Time */}
            <Grid item xs={6} sx={{ padding: "30px", position: "relative" }}>
              <Typography variant="body2" color="#95919D" mb={1}>
                Select Time
              </Typography>
              <TextField
                fullWidth
                value={startTime}
                variant="standard"
                onClick={() => timeInputRef.current?.showPicker()}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <label htmlFor="start-time-picker">
                        <AccessTimeIcon
                          sx={{ color: "#1976d2", cursor: "pointer" }}
                        />
                      </label>
                    </InputAdornment>
                  ),
                }}
              />
              <input
                type="time"
                id="start-time-picker"
                ref={timeInputRef}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                style={{
                  position: "absolute",
                  top: 50,
                  left: 0,
                  opacity: 0,
                  width: 0,
                  height: 0,
                  pointerEvents: "auto",
                }}
              />
            </Grid>
          </Box>

          {/* Footer Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              px: 3,
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCloseSchedule}
              disabled={loader}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            {loader ? (
              <CircularProgress size="30px" />
            ) : (
              <Button
                variant="contained"
                onClick={schedulePost}
                sx={{
                  backgroundColor: "#2D76DC",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#1c5db8" },
                }}
              >
                Schedule
              </Button>
            )}
          </Box>
        </Box>
      </Dialog>

      <Dialog
        elevation={0}
        open={open}
        PaperProps={{ sx: { p: 0, m: 0, borderRadius: 0 } }}
        onClose={handleCloseMainPopup}
        fullScreen
      >
        <Box sx={{ backgroundColor: "#f6f3ff", height: "auto", pb: 7 }}>
          <Box
            bgcolor={"#ffffff"}
            boxShadow={"0px 4px 20px rgba(236, 236, 236, 0.25)"}
            display="flex"
            sx={{ pl: { xs: "16px", sm: "50px" }, pr: { xs: "16px", sm: "20px" }, pt: "13px", pb: "13px" }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              color="rgba(59, 59, 59, 1)"
              fontWeight={500}
            >
              {title}
            </Typography>
            <IconButton
              sx={{ width: "24px", height: "24px" }}
              onClick={handleCloseMainPopup}
            >
              <CloseIcon sx={{ color: "rgba(138, 138, 138, 1)" }} />
            </IconButton>
          </Box>

          <DialogContent sx={{ pl: { xs: 1.5, sm: 6 }, pr: { xs: 1.5, sm: 6 }, pt: "10px" }}>
            <Box
              bgcolor={"#FFFFFF"}
              sx={{ padding: { xs: "10px 12px", sm: "15px 25px" }, borderRadius: "16px" }}
              display="flex"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent={"space-between"}
              gap={{ xs: 1.5, sm: 0 }}
              mb={1.2}
            >
              <Box display={"flex"} gap={1} alignItems={"center"} flexWrap="wrap">
                <Typography
                  variant="subtitle2"
                  color="#95919D"
                  fontWeight={500}
                  fontSize={"12px"}
                >
                  Post By,
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontSize={"14px"}
                  color="#3B3B3B"
                  fontWeight={500}
                >
                  @{postReviewData?.preparedPost?.createdBy?.name || ""}
                </Typography>
                <CategoryListing postReviewData={postReviewData} />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    justifyContent: { xs: "space-between", sm: "flex-end" },
                    gap: "6px",
                    width: "100%"
                  }}
                >
                  <Typography
                    color="#3B3B3B"
                    fontSize={"13px"}
                    fontWeight={400}
                    display={"flex"}
                    alignItems={"center"}
                    gap={"4px"}
                  >
                    Share to all & earn:{" "}
                    <img src={star} width={"18px"} height={"18px"} />
                    <strong style={{ color: "#8296B2", fontSize: "14px" }}>
                      {totalCoins}
                    </strong>
                  </Typography>

                  <Box display="flex" gap={0.5} alignItems="center">
                    <Button
                      sx={{
                        backgroundColor: "#f6f1ff",
                        color: "#0047AB",
                        textTransform: "none",
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                        fontWeight: 500,
                        fontSize: "12px",
                      }}
                      onClick={handleCloseMainPopup}
                      disabled={loader}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{
                        backgroundColor: "#f6f1ff",
                        color: "#0047AB",
                        textTransform: "none",
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                        fontWeight: 500,
                        fontSize: "12px",
                      }}
                      onClick={() => setSchedule(true)}
                      disabled={loader}
                    >
                      Schedule
                    </Button>
                    {loader ? (
                      <CircularProgress size="26px" />
                    ) : (
                      <Button
                        sx={{
                          backgroundColor: "#0047AB",
                          textTransform: "none",
                          borderRadius: 2,
                          px: 2,
                          py: 0.5,
                          color: "#fff",
                          fontSize: "12px",
                        }}
                        onClick={type === "post" ? handlePost : handleShare}
                        disabled={loader || !Object.values(platformData).some(data => data.enabled)}
                      >
                        {type === "post" ? "Post" : "Share"}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            {variations.length > 1 && (
              <Box mb={2} mt={2}>
                <Typography variant="subtitle2" color="#3B3B3B" mb={1} fontWeight={500}>
                  Select Variation
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {variations.map((v, idx) => (
                    <Button
                      key={idx}
                      variant={selectedVariationIndex === idx ? "contained" : "outlined"}
                      onClick={() => setSelectedVariationIndex(idx)}
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        backgroundColor: selectedVariationIndex === idx ? "#0047AB" : "transparent",
                        color: selectedVariationIndex === idx ? "#fff" : "#0047AB",
                        borderColor: "#0047AB",
                        "&:hover": {
                          backgroundColor: selectedVariationIndex === idx ? "#003580" : "#E7F1FF",
                        }
                      }}
                    >
                      Variation {idx + 1}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

            <Grid container spacing={1.2}>
              {platforms?.map((platform, index) => {
                const currentMedia =
                  platform?.sampleImages?.[currentIndexes[index]] || {};
                const isVideo = currentMedia?.mediaType?.toUpperCase() === "VIDEO";
                const isDocument = currentMedia?.mediaType?.toUpperCase() === "DOCUMENT" ||
                  (currentMedia?.fileUrl && (
                    currentMedia.fileUrl.toLowerCase().endsWith(".pdf") ||
                    currentMedia.fileUrl.toLowerCase().endsWith(".doc") ||
                    currentMedia.fileUrl.toLowerCase().endsWith(".docx") ||
                    currentMedia.fileUrl.toLowerCase().endsWith(".pptx")
                  ));
                const isBunnyVideo = isBunnyStreamMedia(currentMedia);
                return (
                  <Grid item xs={12} md={6} key={index}>
                    {platformData[platform.name]?.enabled ? (
                      <Box
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          backgroundColor: "#fff",
                          borderRadius: 3,
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: 1.5,
                          boxShadow: "0px 4px 20px rgba(236, 236, 236, 0.25)",
                        }}
                      >
                        {platform?.sampleImages?.length > 0 && (
                          <Box
                            sx={{
                              width: { xs: "100%", sm: "220px" },
                              height: { xs: "180px", sm: "235px" },
                              borderRadius: 3,
                              overflow: "hidden",
                              position: "relative",
                              flexShrink: 0,
                            }}
                          >
                            {isDocument ? (
                              <Box
                                sx={{
                                  position: "relative",
                                  width: "100%",
                                  height: "100%",
                                  borderRadius: 3,
                                  overflow: "hidden",
                                  backgroundColor: "#f8fafc",
                                  border: "1px solid #BFDBFE",
                                }}
                              >
                                {(() => {
                                  const isDirectPdf = currentMedia?.fileUrl?.toLowerCase().endsWith(".pdf") || currentMedia?.fileUrl?.startsWith("blob:");
                                  const encodedUrl = encodeURIComponent(currentMedia?.fileUrl || "");
                                  const viewerUrl = isDirectPdf
                                    ? `${currentMedia.fileUrl}#toolbar=0&navpanes=0&scrollbar=0`
                                    : `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
                                  return (
                                    <>
                                      <iframe
                                        src={viewerUrl}
                                        title={currentMedia.fileName || "Document Preview"}
                                        style={{
                                          width: "calc(100% + 15px)",
                                          height: "calc(100% + 55px)",
                                          border: 0,
                                          position: "absolute",
                                          top: 0,
                                          left: 0,
                                        }}
                                      />
                                      <IconButton
                                        component="a"
                                        href={currentMedia.fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                          position: "absolute",
                                          top: 6,
                                          right: 6,
                                          backgroundColor: "rgba(255,255,255,0.9)",
                                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                          "&:hover": { backgroundColor: "#ffffff" },
                                        }}
                                        size="small"
                                      >
                                        <OpenInNewIcon sx={{ color: "#1D4ED8", fontSize: 16 }} />
                                      </IconButton>
                                    </>
                                  );
                                })()}
                              </Box>
                            ) : isVideo ? (
                              <Box
                                sx={{
                                  position: "relative",
                                  width: "100%",
                                  height: "100%",
                                }}
                              >
                                {isBunnyVideo ? (
                                  activeBunnyPlayers?.[index] ? (
                                    <iframe
                                      src={getBunnyEmbedUrl(
                                        getResolvedVideoUrl(currentMedia),
                                        { autoplay: true }
                                      )}
                                      title={platform.name}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        border: 0,
                                        display: "block",
                                        borderRadius: 12,
                                        background: "#000",
                                      }}
                                      allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
                                      allowFullScreen
                                    />
                                  ) : (
                                    <>
                                      <img
                                        src={getResolvedPosterUrl(
                                          currentMedia,
                                          BLACK_THUMBNAIL
                                        )}
                                        alt="preview"
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = BLACK_THUMBNAIL;
                                        }}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                          borderRadius: 12,
                                          background: "#000",
                                        }}
                                      />
                                      <IconButton
                                        onClick={() =>
                                          handleActivateBunnyPlayer(index)
                                        }
                                        sx={{
                                          position: "absolute",
                                          top: "50%",
                                          left: "50%",
                                          transform: "translate(-50%, -50%)",
                                          background: "rgba(0,0,0,0.5)",
                                          color: "#fff",
                                          fontSize: "15px",
                                          padding: "6px 8px 5px 12px",
                                          "&:hover": {
                                            background: "rgba(0,0,0,0.7)",
                                          },
                                        }}
                                      >
                                        ▶
                                      </IconButton>
                                    </>
                                  )
                                ) : (
                                  <>
                                    <video
                                      ref={(el) => (videoRefs.current[index] = el)}
                                      src={getResolvedVideoUrl(currentMedia)}
                                      poster={getResolvedPosterUrl(currentMedia, BLACK_THUMBNAIL)}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        borderRadius: 12,
                                        background: "#000",
                                      }}
                                      onPause={() => setPlayingIndex(null)}
                                    />
                                    {playingIndex !== index && (
                                      <IconButton
                                        onClick={() => handlePlayToggle(index)}
                                        sx={{
                                          position: "absolute",
                                          top: "50%",
                                          left: "50%",
                                          transform: "translate(-50%, -50%)",
                                          background: "rgba(0,0,0,0.5)",
                                          color: "#fff",
                                          fontSize: "15px",
                                          padding: "6px 8px 5px 12px",
                                          "&:hover": {
                                            background: "rgba(0,0,0,0.7)",
                                          },
                                        }}
                                      >
                                        ▶
                                      </IconButton>
                                    )}
                                  </>
                                )}
                              </Box>
                            ) : (
                              <img
                                src={getResolvedImageUrl(currentMedia, noImage)}
                                alt="preview"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: 12,
                                }}
                              />
                            )}
                            {platform?.sampleImages?.length > 1 && (
                              <>
                                <IconButton
                                  sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: 8,
                                    transform: "translateY(-50%)",
                                    backgroundColor: "rgba(0,0,0,0.4)",
                                    color: "#fff",
                                    width: "28px",
                                    height: "28px",
                                    zIndex: 4,
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.6)",
                                    },
                                  }}
                                  onClick={() => handleImageChange(index, -1)}
                                >
                                  <NavigateBeforeIcon
                                    sx={{ color: "#fff", fontSize: "18px" }}
                                  />
                                </IconButton>

                                {/* Next Button */}
                                <IconButton
                                  sx={{
                                    position: "absolute",
                                    top: "50%",
                                    right: 8,
                                    transform: "translateY(-50%)",
                                    backgroundColor: "rgba(0,0,0,0.4)",
                                    color: "#fff",
                                    width: "28px",
                                    height: "28px",
                                    zIndex: 4,
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.6)",
                                    },
                                  }}
                                  onClick={() => handleImageChange(index, 1)}
                                >
                                  <NavigateNextIcon
                                    sx={{ color: "#fff", fontSize: "18px" }}
                                  />
                                </IconButton>

                                {/* Dots */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: 0.8,
                                    position: "absolute",
                                    bottom: 8,
                                    left: 0,
                                    right: 0,
                                    zIndex: 4,
                                  }}
                                >
                                  {platform?.sampleImages?.map((_, dotIndex) => (
                                    <Box
                                      key={dotIndex}
                                      sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: "50%",
                                        backgroundColor:
                                          dotIndex === currentIndexes[index]
                                            ? "#2D76DC"
                                            : "rgba(255, 255, 255, 0.7)",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                                      }}
                                    />
                                  ))}
                                </Box>
                              </>
                            )}

                            {/* Update Button */}
                            <Button
                              fullWidth
                              sx={{
                                mt: 1,
                                backgroundColor: "#f5f1ff",
                                color: "#3B3B3B",
                                borderRadius: 2,
                                fontSize: "16px",
                                fontWeight: 500,
                                textTransform: "none",
                                gap: 1,
                                py: 1,
                              }}
                              startIcon={
                                <img
                                  src={edit}
                                  width={"16px"}
                                  height={"16px"}
                                  alt="edit"
                                />
                              }
                            >
                              Update Image
                            </Button>
                          </Box>
                        )}

                        {/* Right Content */}
                        <Box
                          sx={{
                            flex: 1,
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                  backgroundColor: "#2D76DC",
                                  borderRadius: "0px 20px 20px 0px",
                                  pr: 2,
                                  pl: 1,
                                  py: 0.5,
                                  color: "#fff",
                                  textTransform: "none",
                                }}
                              >
                                <img
                                  src={platform?.icon}
                                  width={"13px"}
                                  height={"13px"}
                                />
                                <Typography
                                  color="#fff"
                                  fontWeight={500}
                                  fontSize={"12px"}
                                >
                                  {platform?.name}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <img
                                  src={star}
                                  width={"15px"}
                                  height={"15px"}
                                />
                                <Typography
                                  color="#8296B2"
                                  fontSize={"13px"}
                                  fontWeight={400}
                                >
                                  {platform?.points || ""}
                                </Typography>
                              </Box>
                            </Box>
                            <Switch
                              size="small"
                              checked={platformData[platform.name]?.enabled}
                              onChange={() =>
                                handleTogglePlatform(platform.name)
                              }
                            />
                          </Box>

                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mt={"10px"}
                          >
                            <Avatar
                              src={
                                postReviewData?.preparedPost?.createdBy
                                  ?.profileImageUrl || ""
                              }
                              sx={{ width: "30px", height: "30px" }}
                            />
                            <Typography
                              color="#252525"
                              fontSize={"12px"}
                              fontWeight={500}
                            >
                              @
                              {postReviewData?.preparedPost?.createdBy?.name ||
                                ""}
                            </Typography>
                          </Box>
                          {type !== "post" && (
                            <>
                              <Box
                                sx={{
                                  mt: "10px",
                                  borderRadius: "12px",
                                  backgroundColor: "#f7f8fa",
                                  border: "1px solid #e0e0e0",
                                  px: "8px",
                                  py: "8px",
                                  display: "flex",
                                  flexDirection: "row",
                                  flexWrap: "wrap",
                                  alignItems: "flex-start",
                                  gap: 1,
                                }}
                              >
                                <TextField
                                  variant="standard"
                                  placeholder="Write Ai Caption for this post..."
                                  fullWidth
                                  multiline
                                  value={platformData[platform.name]?.thoughts}
                                  onChange={(e) =>
                                    handleThoughtChange(
                                      platform.name,
                                      e.target.value
                                    )
                                  }
                                  InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                      fontSize: "11px",
                                      flex: 1,
                                      backgroundColor: "transparent",
                                    },
                                  }}
                                  sx={{
                                    flex: 1,
                                    minWidth: "200px",
                                    ".MuiInputBase-root": {
                                      backgroundColor: "transparent",
                                    },
                                  }}
                                />
                                {aiLoader[platform.name] ? (
                                  <Button
                                    variant="contained"
                                    disabled
                                    sx={{
                                      width: "132px",
                                      background:
                                        "linear-gradient(to right, #BC77F4, #0047AB)",
                                      color: "#fff !important",
                                      borderRadius: "8px",
                                      textTransform: "none",
                                      fontSize: "13px",
                                      padding: "5px 10px",
                                      boxShadow: "none",
                                      fontWeight: 500,
                                      alignSelf: "flex-end",
                                      cursor: "not-allowed !important",
                                      pointerEvents: "auto !important",

                                      "&:hover": {
                                        background:
                                          "linear-gradient(to right, #8a6be9, #306ddf)",
                                        boxShadow: "none",
                                      },
                                      "&.Mui-disabled": {
                                        opacity: 0.65,
                                        color: "#fff !important",
                                        background:
                                          "linear-gradient(to right, #BC77F4, #0047AB)",
                                        cursor: "not-allowed !important",
                                        pointerEvents: "auto !important",
                                      },
                                    }}
                                  >
                                    ✨ Generating{dots}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="contained"
                                    sx={{
                                      width: "132px",
                                      background:
                                        "linear-gradient(to right, #BC77F4, #0047AB)",
                                      color: "#fff",
                                      borderRadius: "8px",
                                      fontSize: "13px",
                                      textTransform: "none",
                                      padding: "5px 10px",
                                      boxShadow: "none",
                                      fontWeight: 500,
                                      alignSelf: "flex-end",

                                      "&:hover": {
                                        background:
                                          "linear-gradient(to right, #8a6be9, #306ddf)",
                                        boxShadow: "none",
                                      },
                                    }}
                                    onClick={() =>
                                      handleToggleEditor(platform.name)
                                    }
                                  >
                                    ✨ Generate
                                  </Button>
                                )}
                              </Box>
                              <Typography
                                textAlign={"end"}
                                variant="body2"
                                color={
                                  getPlatformComposition(
                                    platform.name,
                                    platformData[platform.name]?.thoughts
                                  ).remainingCharacters === 0
                                    ? "#DA4040"
                                    : "#ADA7A7"
                                }
                                fontSize={"10px"}
                                mt={1}
                              >
                                {(() => {
                                  const composition = getPlatformComposition(
                                    platform.name,
                                    platformData[platform.name]?.thoughts
                                  );
                                  return `Remaining: ${composition.remainingCharacters} / ${composition.maxBaseCharacters}`;
                                })()}
                              </Typography>
                            </>
                          )}
                          {(() => {
                            const contentValue =
                              type === "post"
                                ? platformData[platform.name]?.caption
                                : platformData[platform.name]?.thoughts;
                            const composition = getPlatformComposition(
                              platform.name,
                              contentValue
                            );

                            if (composition.suffixMode === "tag") {
                              return (
                                <Typography
                                  variant="body2"
                                  color="#8296B2"
                                  fontSize={"11px"}
                                  mt={1}
                                >
                                  Business page tag will be added: {composition.suffixText}
                                </Typography>
                              );
                            }

                            if (composition.suffixMode === "url") {
                              return (
                                <Typography
                                  variant="body2"
                                  color="#8296B2"
                                  fontSize={"11px"}
                                  mt={1}
                                >
                                  Business page URL will be added: {composition.suffixText}
                                </Typography>
                              );
                            }

                            return (
                              <Typography
                                variant="body2"
                                color="#DA4040"
                                fontSize={"11px"}
                                mt={1}
                              >
                                No business page metadata found for this platform.
                              </Typography>
                            );
                          })()}

                          {/* {platformData[platform.name]?.showEditor && (
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  mb: 0.4,
                                  mt: 1,
                                  fontWeight: 400,
                                  color: "#9C9C9C",
                                  fontSize: "12px",
                                }}
                              >
                                Result Generated
                              </Typography>
                              <EditorBox />
                            </Box>
                          )} */}
                          {/* {type !== "post" && (
                            <Box
                              sx={{
                                mt: "10px",
                                borderRadius: "12px",
                                backgroundColor: "#f7f8fa",
                                border: "1px solid #e0e0e0",
                                px: "8px",
                                py: "8px",
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                alignItems: "flex-start",
                                gap: 1,
                                maxHeight: "100px",
                                overflowY: "auto",
                                scrollbarWidth: "none",
                                msOverflowStyle: "none",
                                "&::-webkit-scrollbar": {
                                  display: "none",
                                },
                              }}
                            >
                              <Typography fontSize={"12px"}>
                                {platformData[platform.name]?.des}
                              </Typography>
                            </Box>
                          )} */}
                          {type === "post" && (
                            <>
                              <Typography
                                textAlign={"end"}
                                variant="body2"
                                color={
                                  getPlatformComposition(
                                    platform.name,
                                    platformData[platform.name]?.caption
                                  ).remainingCharacters === 0
                                    ? "#DA4040"
                                    : "#ADA7A7"
                                }
                                fontSize={"10px"}
                                mt={1}
                              >
                                {(() => {
                                  const composition = getPlatformComposition(
                                    platform.name,
                                    platformData[platform.name]?.caption
                                  );
                                  return `Remaining: ${composition.remainingCharacters} / ${composition.maxBaseCharacters}`;
                                })()}
                              </Typography>

                              <Box
                                sx={{
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                  scrollbarWidth: "none",
                                  msOverflowStyle: "none",

                                  "&::-webkit-scrollbar": {
                                    display: "none",
                                  },
                                }}
                              >
                                <CleanCaptionInput
                                  value={platformData[platform.name]?.caption}
                                  onChange={(newVal) =>
                                    handleCaptionChange(platform.name, newVal)
                                  }
                                />
                              </Box>
                            </>
                          )}
                          {/* {platform?.name === "Instagram" && (
                          <>
                            <Typography
                              textAlign={"end"}
                              variant="body2"
                              color="#ADA7A7"
                              fontSize={"10px"}
                              mt={1}
                            >
                              Max HashTags: 30
                            </Typography>

                            <HashtagInput
                              value={platformData[platform.name]?.hashtags}
                              onChange={(tags) =>
                                handleHashtagsChange(platform.name, tags)
                              }
                            />
                          </>
                        )} */}
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: "100px",
                          background: "#ffffff",
                          borderRadius: 3,
                          px: 2,
                          // py: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",

                          boxShadow: "0px 4px 20px rgba(236, 236, 236, 0.25)",
                        }}
                      >
                        <Box
                          display="flex"
                          width={"100%"}
                          justifyContent={"space-between"}
                          alignItems="center"
                          gap={1}
                        >
                          <Box display={"flex"} gap={1}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                backgroundColor: "#2D76DC",
                                borderRadius: "0px 20px 20px 0px",
                                pr: 2,
                                pl: 2,
                                py: 1,
                                color: "#fff",
                                textTransform: "none",
                              }}
                            >
                              <img
                                src={platform?.icon}
                                width={"13px"}
                                height={"13px"}
                              />
                              <Typography
                                color="#fff"
                                fontWeight={500}
                                fontSize={"12px"}
                              >
                                {platform?.name}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <img src={star} width={"20px"} height={"20px"} />
                              <Typography
                                color="#8296B2"
                                fontSize={"16px"}
                                fontWeight={400}
                              >
                                {platform?.points || ""}
                              </Typography>
                            </Box>
                          </Box>

                          <Switch
                            size="small"
                            checked={false}
                            onChange={() => handleTogglePlatform(platform.name)}
                          />
                        </Box>
                      </Box>
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>
        </Box>
      </Dialog>
      {/* Connect Account Dialog */}
      <Dialog
        open={!!connectDialogPlatform}
        onClose={() => setConnectDialogPlatform(null)}
        PaperProps={{
          sx: { borderRadius: "16px", padding: { xs: "12px 14px", sm: "16px 20px" }, maxWidth: "420px", width: "100%" },
        }}
      >
        <IconButton
          onClick={() => setConnectDialogPlatform(null)}
          sx={{ position: "absolute", top: { xs: 8, sm: 16 }, right: { xs: 8, sm: 16 } }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          sx={{ fontWeight: "500", color: "#484848", fontSize: { xs: "17px", sm: "20px" }, p: 0, mb: 1, pr: 4 }}
        >
          Account Not Connected
        </DialogTitle>
        <DialogContent sx={{ padding: { xs: "10px 0px", sm: "20px 5px" } }}>
          <Typography
            sx={{ fontSize: { xs: "14px", sm: "16px" }, color: "#616161", mb: { xs: 2, sm: 3 }, textAlign: "start" }}
          >
            Please connect your {connectDialogPlatform} profile in case you want to share.
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={{ xs: 1, sm: 2 }}>
            <Button
              onClick={() => setConnectDialogPlatform(null)}
              sx={{
                backgroundColor: "#F4F0FF",
                color: "#0D47A1",
                textTransform: "none",
                fontSize: { xs: "13px", sm: "16px" },
                fontWeight: 500,
                borderRadius: "8px",
                px: { xs: 2, sm: 4 },
                py: 0.8,
                "&:hover": { backgroundColor: "#E9D5FF" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConnectDialogPlatform(null);
                onClose();
                const isAdmin = JSON.parse(sessionStorage.getItem("role") || "[]").flat().includes("ROLE_ADMIN");
                const basePath = isAdmin ? "/admin" : "/employees";
                navigate(`${basePath}/settings`, { state: { openPersonalAccounts: true } });
              }}
              sx={{
                backgroundColor: "#003db3",
                color: "#fff",
                textTransform: "none",
                fontSize: { xs: "13px", sm: "16px" },
                fontWeight: 500,
                borderRadius: "8px",
                px: { xs: 2, sm: 4 },
                py: 0.8,
                "&:hover": { backgroundColor: "#002f8c" },
              }}
            >
              Connect Account
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Tea Loading Modal for AI Generation ─────────────────────── */}
      <TeaLoadingModal
        open={showGeneratingPopup && Boolean(aiLoader && Object.values(aiLoader).some(Boolean))}
        onClose={() => setShowGeneratingPopup(false)}
        message={"We're working on your post — till then,\nhave a cup of tea."}
      />
    </>
  );
};

export default SharePostPopup;
